import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

// --- Shared microtask batching ---
// Multiple useUrlState setters called in the same event handler each have their
// own setSearchParams (from separate useSearchParams hooks). Calling them
// individually races — each reads the same pre-update URL, so only the last
// one's result survives. This queue collects all updates and flushes them in a
// single setSearchParams call on the next microtask.
let pendingBatch = null;       // Map<key, { value, isDefault }>
let batchSetter = null;        // The setSearchParams function to use for flush

function enqueueUrlUpdate(key, value, isDefault, setSearchParams) {
  if (!pendingBatch) {
    pendingBatch = new Map();
    queueMicrotask(flushUrlBatch);
  }
  pendingBatch.set(key, { value, isDefault });
  // Always keep the latest setter — they all navigate to the same URL
  batchSetter = setSearchParams;
}

function flushUrlBatch() {
  const updates = pendingBatch;
  const setter = batchSetter;
  pendingBatch = null;
  batchSetter = null;

  if (!updates || updates.size === 0 || !setter) return;

  setter(prev => {
    const next = new URLSearchParams(prev);
    for (const [key, { value, isDefault }] of updates) {
      if (isDefault) {
        next.delete(key);
      } else if (Array.isArray(value)) {
        next.set(key, JSON.stringify(value));
      } else {
        next.set(key, String(value));
      }
    }
    return next;
  }, { replace: true });
}

/**
 * URL search-parameter backed state — drop-in replacement for useSessionState.
 * Default values are omitted from the URL for clean, shareable links.
 * Supports strings, numbers, booleans, arrays (JSON-encoded), and null.
 *
 * Multiple setters called in the same event handler are automatically batched
 * into a single URL update (via microtask queue).
 *
 * @param {string} key - URL parameter name
 * @param {*} defaultValue - Fallback when param is absent
 * @returns {[*, Function]} - Same API as useState / useSessionState
 */
export function useUrlState(key, defaultValue) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Stable ref so the setter identity never changes
  const setRef = useRef(setSearchParams);
  setRef.current = setSearchParams;
  const defaultRef = useRef(defaultValue);

  // Cache for stable array references (prevents new object on every render)
  const cacheRef = useRef({ raw: undefined, value: undefined });

  // --- Deserialize from URL ---
  const raw = searchParams.get(key);
  let value;

  if (raw === null) {
    value = defaultRef.current;
  } else if (Array.isArray(defaultRef.current)) {
    // Return cached array if the raw string hasn't changed
    if (cacheRef.current.raw === raw) {
      value = cacheRef.current.value;
    } else {
      try { value = JSON.parse(raw); } catch { value = defaultRef.current; }
      cacheRef.current = { raw, value };
    }
  } else if (typeof defaultRef.current === 'number') {
    const n = Number(raw);
    value = Number.isNaN(n) ? defaultRef.current : n;
  } else if (typeof defaultRef.current === 'boolean') {
    value = raw === 'true';
  } else {
    // string or null default
    value = raw;
  }

  // --- Setter (batched via microtask queue) ---
  const setValue = useCallback((newValue) => {
    const def = defaultRef.current;

    // Determine if the value matches the default → omit from URL
    let isDefault;
    if (Array.isArray(def)) {
      isDefault = Array.isArray(newValue)
        ? newValue.length === 0 || JSON.stringify(newValue) === JSON.stringify(def)
        : newValue == null;
    } else if (typeof def === 'boolean') {
      isDefault = newValue === def;
    } else {
      isDefault = newValue === def || newValue === '' || newValue == null;
    }

    enqueueUrlUpdate(key, newValue, isDefault, setRef.current);
  }, [key]);

  return [value, setValue];
}
